import type {
  BeatstageBundleV1,
  ClickPoint,
  ChartData,
  PathSegment,
  PointerSample,
  BpmSegment,
} from './types';

import LevelMakerWorker from './workers/levelMakerWorker?worker';

export { STAGE } from './types';
export type {
  BeatstageBundleV1,
  ClickPoint,
  ChartData,
  PathSegment,
  BpmSegment,
};

// Parameters used by multiple worker operations
interface ChartParams {
  density: number;
  smoothing: number;
  minSpacingPx: number;
  pathThresholdPx: number;
}

// Message mapping -----------------------------------------------------------

interface DetectBpmIn {
  type: 'detectBpm';
  sampleRate: number;
  channelData: Float32Array;
}

interface DetectBpmOut {
  type: 'detectBpm';
  bpm: number;
}

interface TempoTrackIn {
  type: 'tempoTrack';
  sampleRate: number;
  channelData: Float32Array;
}

interface TempoTrackOut {
  type: 'tempoTrack';
  track: Array<{ tMs: number; bpm: number; periodFrames: number }>;
}

interface BuildGridIn {
  type: 'buildGrid';
  sampleRate: number;
  channelData: Float32Array;
}

interface BuildGridOut {
  type: 'buildGrid';
  grid: number[];
}

interface AnalyzeOnsetsIn {
  type: 'analyzeOnsets';
  sampleRate: number;
  channelData: Float32Array;
  threshold?: number;
  refractory?: number;
}

interface AnalyzeOnsetsOut {
  type: 'analyzeOnsets';
  onsets: number[];
}

interface OnsetEnergiesIn {
  type: 'onsetEnergies';
  sampleRate: number;
  channelData: Float32Array;
  grid: number[];
}

interface OnsetEnergiesOut {
  type: 'onsetEnergies';
  energies: number[];
}

interface GenAutoChartIn {
  type: 'genAutoChart';
  grid: number[];
  energies: number[];
  params: ChartParams;
}

interface GenAutoChartOut {
  type: 'genAutoChart';
  chart: ChartData;
}

interface DirectedChartIn {
  type: 'directedChart';
  sampleRate: number;
  channelData: Float32Array;
  width: number;
  height: number;
}

interface DirectedChartOut {
  type: 'directedChart';
  chart: ChartData;
  beats: number[];
  subbeats: number[];
}

interface ClickFromRecordingIn {
  type: 'clickFromRecording';
  grid: number[];
  samples: PointerSample[];
  params: ChartParams;
}

interface ClickFromRecordingOut {
  type: 'clickFromRecording';
  paths: PathSegment[];
}

interface SmoothPathsIn {
  type: 'smoothPaths';
  points: ClickPoint[];
  smoothing: number;
}

interface SmoothPathsOut {
  type: 'smoothPaths';
  paths: PathSegment[];
}

interface BuildHitObjectsIn {
  type: 'buildHitObjects';
  chart: ChartData;
  segments: BpmSegment[];
}

interface BuildHitObjectsOut {
  type: 'buildHitObjects';
  hitObjects: unknown[];
}

interface SerializeBundleIn {
  type: 'serialize';
  bundle: BeatstageBundleV1;
}

interface SerializeBufferIn {
  type: 'serialize';
  buffer: ArrayBuffer;
}

type SerializeIn = SerializeBundleIn | SerializeBufferIn;

interface SerializeOut {
  type: 'serialize';
  encoded: string;
  hash: string;
  size: number;
}

interface HashIn {
  type: 'hash';
  buffer: ArrayBuffer;
}

interface HashOut {
  type: 'hash';
  hash: string;
}

interface GzipBase64In {
  type: 'gzipBase64';
  buffer: ArrayBuffer;
}

interface GzipBase64Out {
  type: 'gzipBase64';
  encoded: string;
}

type MsgInMap = {
  detectBpm: DetectBpmIn;
  tempoTrack: TempoTrackIn;
  buildGrid: BuildGridIn;
  analyzeOnsets: AnalyzeOnsetsIn;
  onsetEnergies: OnsetEnergiesIn;
  genAutoChart: GenAutoChartIn;
  directedChart: DirectedChartIn;
  clickFromRecording: ClickFromRecordingIn;
  smoothPaths: SmoothPathsIn;
  buildHitObjects: BuildHitObjectsIn;
  serialize: SerializeIn;
  hash: HashIn;
  gzipBase64: GzipBase64In;
};

type MsgOutMap = {
  detectBpm: DetectBpmOut;
  tempoTrack: TempoTrackOut;
  buildGrid: BuildGridOut;
  analyzeOnsets: AnalyzeOnsetsOut;
  onsetEnergies: OnsetEnergiesOut;
  genAutoChart: GenAutoChartOut;
  directedChart: DirectedChartOut;
  clickFromRecording: ClickFromRecordingOut;
  smoothPaths: SmoothPathsOut;
  buildHitObjects: BuildHitObjectsOut;
  serialize: SerializeOut;
  hash: HashOut;
  gzipBase64: GzipBase64Out;
};

export type MsgIn = MsgInMap[keyof MsgInMap];
export type MsgOut =
  | MsgOutMap[keyof MsgOutMap]
  | { type: 'log'; message: string }
  | { type: 'error'; error: string };

// Messages exchanged with the worker are tagged with an incrementing id so
// multiple requests can be processed concurrently without clobbering each
// other's event listeners.
type WorkerMsgIn = MsgIn & { id: number };
type WorkerMsgOut = MsgOut & { id: number };

// Worker runner -------------------------------------------------------------

let worker: Worker | undefined;
let nextId = 0;

function getWorker(): Worker {
  worker ??= new LevelMakerWorker();
  return worker;
}

export function runWorker<T extends MsgIn['type']>(
  msg: Extract<MsgIn, { type: T }>,
  onLog?: (msg: string) => void,
  transfer?: Transferable[],
): Promise<Extract<MsgOut, { type: T }>> {
  const id = nextId++;
  return new Promise((resolve, reject) => {
    onLog?.(`Starting worker for ${msg.type}`);
    const worker = getWorker();

    const cleanup = () => {
      worker.removeEventListener('message', handleMessage);
      worker.removeEventListener('error', handleError);
      worker.removeEventListener('messageerror', handleMessageError);
    };

    const handleMessage = (e: MessageEvent<WorkerMsgOut>) => {
      const data = e.data;
      if (data.id !== id) return; // Ignore messages for other requests
      if (data.type === 'log') {
        onLog?.(data.message);
        return;
      }
      cleanup();
      onLog?.(`Worker finished: ${data.type}`);
      if (data.type === 'error') {
        reject(new Error(data.error));
      } else {
        resolve(data as Extract<MsgOut, { type: T }>);
      }
    };

    const handleError = (e: ErrorEvent | Event) => {
      cleanup();
      const err =
        e instanceof ErrorEvent ? e.error ?? new Error(e.message) : new Error(String(e));
      onLog?.(`Worker error: ${err}`);
      reject(err);
    };

    const handleMessageError = () => {
      cleanup();
      onLog?.('Worker message error');
      reject(new Error('Worker message error'));
    };

    worker.addEventListener('message', handleMessage);
    worker.addEventListener('error', handleError);
    worker.addEventListener('messageerror', handleMessageError);
    onLog?.(`Posting message to worker: ${msg.type}`);
    worker.postMessage({ ...msg, id } as WorkerMsgIn, transfer ?? []);
  });
}

export {};

